import { Modal, Stack, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useCreateLogMutation } from "src/api/services/logsApi";
import { useVerifyCheckoutSessionMutation } from "src/api/services/paymentsApi";
import Spinner from "src/components/Loading/Spinner";
import { useIsMobile } from "src/hooks/useIsMobile";
import { mobileSx, desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  onClose: () => void;
  onError: (error: string) => void;
  handleSuccess: ({
    invoiceId,
    paymentMethodId,
  }: {
    invoiceId?: string;
    paymentMethodId?: string;
  }) => Promise<void>;
}

const PaymentValidationModal = ({ onClose, onError, handleSuccess }: Props) => {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [verify] = useVerifyCheckoutSessionMutation();
  const [createLog] = useCreateLogMutation();

  const verification = useCallback(async () => {
    const checkoutSession = searchParams.get("checkout_session");

    try {
      if (checkoutSession == null) {
        createLog({
          level: "error",
          data: {
            message: `No checkout session provided for payment verification`,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          },
        });
        onError("No checkout session provided");
        onClose();
        return;
      }

      const data = await verify(checkoutSession).unwrap();

      if (data == null || data.is_valid !== true) {
        onError(
          "Could not verify payment. Please contact support if your payment was processed but you see this message.",
        );
        onClose();
        return;
      }

      await handleSuccess({
        invoiceId: checkoutSession,
      });
    } catch (err) {
      createLog({
        level: "error",
        data: {
          message: `Error during payment verification for checkout session ${checkoutSession}: ${JSON.stringify(
            err,
          )}`,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
      });
      onError("Error during payment verification");
    } finally {
      onClose();
    }
  }, [createLog, handleSuccess, onClose, onError, searchParams, verify]);

  useEffect(() => {
    verification();
  }, []);

  return (
    <Modal open={true}>
      <Stack sx={isMobile ? mobileSx : desktopSx} gap={3} alignItems="center">
        <Typography variant="h4">Verifying...</Typography>
        <Spinner />
      </Stack>
    </Modal>
  );
};

export default PaymentValidationModal;
